import { createContext, ReactNode, useEffect } from 'react';
import { useImmerReducer } from 'use-immer';

import { requestService } from 'shared/utils/services/requestService';
import {
  IGiftCardsDispatch,
  giftCardsReducer,
  IGiftCardsState,
  GiftCardsActions,
} from 'gift-cards/state/giftCardsReducer';
import { IGroupItems } from 'gift-cards/types';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { allPromiseSettled } from 'shared/utils/allPromiseSettled';

export const GiftCardsStateContext = createContext<IGiftCardsState>(null);
export const GiftCardsDispatchContext = createContext<IGiftCardsDispatch>(null);

interface IGiftCardsProvider {
  children: ReactNode;
}

interface PromiseFulfilledResult<T> {
  status: 'fulfilled';
  value: T;
}

export const GiftCardsProvider = ({ children }: IGiftCardsProvider) => {
  const initialValues = {
    spaceSections: {},
    giftCardSelection: true,
    selectedSpaceId: null,
    spaces: {},
    spaceInMobileEditMode: null,
    selectedItemId: null,
  };

  const [state, dispatch] = useImmerReducer(giftCardsReducer, initialValues);

  useEffect(() => {
    try {
      const getSpaces = async () => {
        const groups = await requestService({
          url: '/web-api/v1/spaces',
          method: 'get',
        });

        const giftCardGroups = groups.dataList.filter(
          (group) => group.campaignName === 'Gift Cards',
        );

        const getInitialItems = giftCardGroups.map(async (gcGroup) => {
          return requestService({
            url: `/web-api/v1/spaces/items/random?groupId=${String(gcGroup.id)}&quantity=${Number(
              4,
            )}`,
            method: 'get',
          });
        });

        const initialItems = await allPromiseSettled(getInitialItems);

        const initialItemsMap = {};
        for (const [index, group] of giftCardGroups.entries()) {
          initialItemsMap[group.id] = (
            initialItems[index] as PromiseFulfilledResult<{ dataList: IGroupItems[] }>
          ).value.dataList;
        }

        dispatch({
          type: GiftCardsActions.setInitialSpacesState,
          payload: { giftCardGroups, initialItems: initialItemsMap },
        });
      };
      void getSpaces();
    } catch (error) {
      bugsnag(error, { context: 'Error fetching initial space state' });
    }
  }, []);

  return (
    <GiftCardsDispatchContext.Provider value={dispatch}>
      <GiftCardsStateContext.Provider value={state}>{children}</GiftCardsStateContext.Provider>
    </GiftCardsDispatchContext.Provider>
  );
};
